/**
 * The coach's read-only tracker views on the Standups screens: the List
 * columns and chips, the Totals view, and the student page's Tracker tab.
 * Nothing here can edit, log, delete or import.
 */

import { ComponentChildren } from 'preact';
import { useState } from 'preact/hooks';
import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { BtnSecondary, Button } from '@components/buttons';
import { DefaultSpinner } from '@components/spinner';
import { IcoDownload } from '@components/icons';
import { participantChannels, TrackerConfig } from 'shared/tracker-config';
import { cplOf, scoreboard, TrackerRollup } from 'shared/tracker-rollup';
import { ALL_CHANNELS_LABEL, leadsSetUpLine } from 'shared/tracker-leads';
import { betOf, fmtMoney, fmtTrackerDate, personName } from 'shared/tracker-people';
import {
  betData,
  betsListLine,
  betsScoreboardLine,
  breakdownBets,
  scoreboardLine,
} from 'shared/tracker-summary';
import {
  closeChoiceNote,
  coachPeopleGroups,
  CoachTrackerStudent,
  fmtWeekRange,
  participantWeekRows,
  trackerChips,
  trackerRollupColumns,
  weekLabel,
} from 'shared/tracker-coach';
import { PersonRowView } from '../student-tracker';
import { Facts, Timeline } from '../student-tracker/person-panel';
import { BetSwitch } from '../student-tracker/bets';
import { ByChannelTable, channelPath, TrendTable } from '../student-tracker/leads';

export type TrackerOverview = RpxResponse<typeof rpx.tracker.getCourseTrackerOverview>;
export type TrackerOverviewOn = Extract<TrackerOverview, { enabled: true }>;
export type CoachStudentTracker = RpxResponse<typeof rpx.tracker.getStudentTrackerForCoach>;
type OpenTracker = Extract<CoachStudentTracker, { ended: false }>;

const DOWNLOAD_CLASS =
  'inline-flex items-center gap-1 text-sm text-gray-700 border rounded-md px-3 py-1.5 bg-white hover:bg-gray-50';

export function firstNameOf(name: string) {
  return name.trim().split(/\s+/)[0] || name;
}

function isSequence(config: TrackerConfig) {
  return config.channels.some((c) => c.flow === 'sequence');
}

type ListColumn = {
  key: string;
  label: string;
  title?: string;
  isCloses?: boolean;
  unit?: 'cents';
  value(rollup: TrackerRollup): number | undefined;
};

/** A figure as the tables show it: money, a count, or "–" where it doesn't apply. */
function shown(column: { unit?: 'cents' }, value: number | undefined) {
  if (value === undefined) {
    return '–';
  }
  return column.unit === 'cents' ? fmtMoney(value) : String(value);
}

/** The List's four counts, or each stage of the first channel on a steps-only course. */
export function trackerListColumns(config: TrackerConfig): ListColumn[] {
  const sequence = isSequence(config);
  return trackerRollupColumns(config).map((column) => {
    if (sequence && column.key === 'held') {
      return {
        ...column,
        label: column.label === 'Conversations held' ? 'Convos' : column.label,
        title: column.label,
      };
    }
    if (sequence && column.key === 'closes') {
      return { ...column, isCloses: true };
    }
    return column;
  });
}

export function TrackerCountCell({
  column,
  student,
}: {
  column: ListColumn;
  student?: CoachTrackerStudent;
}) {
  if (!student) {
    return <span class="text-gray-400">—</span>;
  }
  if (column.isCloses && !student.closeAs) {
    return (
      <span class="text-gray-500" title="No close choice yet">
        –
      </span>
    );
  }
  const title =
    column.key === 'cpl' && student.cplTargetCents !== undefined
      ? `Target: ${fmtMoney(student.cplTargetCents)} or less`
      : undefined;
  return (
    <span class="flex flex-col items-center" title={title}>
      <span class="text-base font-semibold text-gray-800 tabular-nums">
        {shown(column, column.value(student.now))}
      </span>
      <span class="text-xs text-gray-500 whitespace-nowrap">
        last week {shown(column, column.value(student.before))}
      </span>
    </span>
  );
}

function Chip({ title, children }: { title: string; children: string }) {
  return (
    <span
      class="inline-block max-w-56 line-clamp-2 align-bottom text-xs px-2 py-0.5 rounded-lg bg-amber-50 text-amber-800"
      title={title}
    >
      {children}
    </span>
  );
}

/** The early-warning chips, per bet once the participant has bets. */
export function TrackerChips({ student }: { student?: CoachTrackerStudent }) {
  if (!student) {
    return;
  }
  return (
    <>
      {trackerChips(student).map((chip) => (
        <Chip key={chip.label} title={chip.title}>
          {chip.label}
        </Chip>
      ))}
    </>
  );
}

type TotalsColumn = { label: string; value(row: TrackerRollup): string };

/** The course's rollup columns, then the money on a sequence course. */
function totalsColumns(config: TrackerConfig): TotalsColumn[] {
  const columns: TotalsColumn[] = trackerRollupColumns(config).map((column) => ({
    label: column.label,
    value: (r) => shown(column, column.value(r)),
  }));
  if (!isSequence(config)) {
    return columns;
  }
  return [
    ...columns,
    { label: 'Booked ($)', value: (r) => fmtMoney(r.bookedCents) },
    { label: 'Collected ($)', value: (r) => fmtMoney(r.collectedCents) },
  ];
}

function sumWeeks(weeks: TrackerOverviewOn['weeks']): TrackerRollup {
  const total: TrackerRollup = {
    sends: 0,
    replies: 0,
    booked: 0,
    held: 0,
    closes: 0,
    bookedCents: 0,
    collectedCents: 0,
    byStage: {},
    steps: 0,
    leads: 0,
  };
  for (const week of weeks) {
    total.steps += week.steps;
    total.leads += week.leads;
    if (week.spendCents !== undefined) {
      total.spendCents = (total.spendCents ?? 0) + week.spendCents;
    }
    total.sends += week.sends;
    total.replies += week.replies;
    total.booked += week.booked;
    total.held += week.held;
    total.closes += week.closes;
    total.bookedCents += week.bookedCents;
    total.collectedCents += week.collectedCents;
    for (const [key, n] of Object.entries(week.byStage)) {
      total.byStage[key] = (total.byStage[key] ?? 0) + n;
    }
  }
  return total;
}

function Th({ children, class: className = '' }: { children: ComponentChildren; class?: string }) {
  return <th class={`px-3 py-2 font-medium ${className}`}>{children}</th>;
}

function Td({ children, class: className = '' }: { children: ComponentChildren; class?: string }) {
  return <td class={`px-3 py-2 border-t align-top ${className}`}>{children}</td>;
}

export function TotalsView({
  courseId,
  overview,
}: {
  courseId: UUID;
  overview: TrackerOverviewOn;
}) {
  const { config, weeks, participants, closeChoices } = overview;
  const columns = totalsColumns(config);
  const current = weeks.find((w) => w.isCurrent);
  const total = sumWeeks(weeks);
  return (
    <div class="flex flex-col gap-3">
      {overview.ended && overview.endDate && (
        <p class="bg-indigo-50 border border-indigo-200 rounded-sm px-4 py-3 text-sm text-gray-800">
          Standups ended {fmtTrackerDate(overview.endDate, overview.today)}. These totals stay for
          analysis. Coaches no longer see individual trackers.
        </p>
      )}
      <div class="flex flex-wrap items-center justify-between gap-2">
        {current && (
          <strong class="text-gray-800">
            {current.logging} of {participants} logging this week
          </strong>
        )}
        {!overview.ended && (
          <a
            class={DOWNLOAD_CLASS}
            download="tracker.csv"
            href={`/csv/courses/${courseId}/tracker.csv`}
          >
            <IcoDownload class="w-4 h-4" />
            Download tracker (CSV)
          </a>
        )}
      </div>
      <div class="overflow-x-auto bg-white rounded-sm border">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
              <Th>Week</Th>
              {columns.map((c) => (
                <Th key={c.label} class="text-right whitespace-nowrap">
                  {c.label}
                </Th>
              ))}
              <Th class="text-right whitespace-nowrap">People logging</Th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((week) => (
              <tr key={week.weekStart}>
                <Td class="whitespace-nowrap">
                  <span class="block font-semibold text-gray-800">
                    {weekLabel(week.weekNumber)}
                  </span>
                  <span class="block text-xs text-gray-500">
                    {fmtWeekRange(week.weekStart)}
                    {week.isCurrent && ' · so far'}
                  </span>
                </Td>
                {columns.map((c) => (
                  <Td key={c.label} class="text-right">
                    {c.value(week)}
                  </Td>
                ))}
                <Td class="text-right whitespace-nowrap">
                  {week.logging} of {participants}
                </Td>
              </tr>
            ))}
            <tr class="font-semibold text-gray-800">
              <Td>Total</Td>
              {columns.map((c) => (
                <Td key={c.label} class="text-right">
                  {c.value(total)}
                </Td>
              ))}
              <Td class="text-right">—</Td>
            </tr>
          </tbody>
        </table>
      </div>
      {participantChannels(config) && <FamilyTable overview={overview} />}
      {isSequence(config) && (
        <p class="text-sm text-gray-600">
          Closes add up each person's own choice: {closeChoices.booked} count booked,{' '}
          {closeChoices.collected} count collected
          {closeChoices.unset > 0 && `, ${closeChoices.unset} haven't chosen`}. The money columns
          don't depend on that choice.
        </p>
      )}
    </div>
  );
}

/**
 * Leads: a row per channel family, to date across participants: steps
 * logged, leads, spend tagged to its channels and cost per lead. Untagged
 * spend is its own row and counts in the total only.
 */
function FamilyTable({ overview }: { overview: TrackerOverviewOn }) {
  const rows = overview.families.map((f) => ({
    label: f.label,
    steps: String(f.steps),
    leads: String(f.leads),
    spend: fmtMoney(f.spendCents),
    cpl: shown({ unit: 'cents' }, cplOf(f.spendCents, f.leads)),
  }));
  if (overview.untaggedSpendCents) {
    rows.push({
      label: ALL_CHANNELS_LABEL,
      steps: '–',
      leads: '–',
      spend: fmtMoney(overview.untaggedSpendCents),
      cpl: '–',
    });
  }
  const steps = overview.families.reduce((n, f) => n + f.steps, 0);
  const leads = overview.families.reduce((n, f) => n + f.leads, 0);
  const spend =
    overview.families.reduce((n, f) => n + f.spendCents, 0) + overview.untaggedSpendCents;
  return (
    <div class="flex flex-col gap-1">
      <strong class="text-gray-800">By channel family, to date</strong>
      <div class="overflow-x-auto bg-white rounded-sm border">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
              <Th>Family</Th>
              <Th class="text-right whitespace-nowrap">Steps logged</Th>
              <Th class="text-right">Leads</Th>
              <Th class="text-right">Spend</Th>
              <Th class="text-right whitespace-nowrap">Cost per lead</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.label}>
                <Td class="font-semibold text-gray-800">{row.label}</Td>
                <Td class="text-right">{row.steps}</Td>
                <Td class="text-right">{row.leads}</Td>
                <Td class="text-right">{row.spend}</Td>
                <Td class="text-right">{row.cpl}</Td>
              </tr>
            ))}
            <tr class="font-semibold text-gray-800">
              <Td>Total</Td>
              <Td class="text-right">{steps}</Td>
              <Td class="text-right">{leads}</Td>
              <Td class="text-right">{fmtMoney(spend)}</Td>
              <Td class="text-right">{shown({ unit: 'cents' }, cplOf(spend, leads))}</Td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** "Coach access ended Mon, Dec 28, the day after standups ended" and who the tracker belongs to. */
export function TrackerEndedState({
  lastDay,
  today,
  name,
}: {
  lastDay: string;
  today: string;
  name: string;
}) {
  const first = firstNameOf(name);
  return (
    <div class="mt-4 flex flex-col gap-1">
      <strong class="text-gray-800">
        Coach access ended {fmtTrackerDate(lastDay, today)}, the day after standups ended
      </strong>
      <span class="text-sm text-gray-600">
        {first}'s tracker belongs to {first} now. Standups stay here. Cohort totals stay in Totals.
      </span>
    </div>
  );
}

/** The weekly table, with the bet switch once the participant has two or more bets. */
function WeeklyTable({
  data,
  first,
  betId,
  setBetId,
}: {
  data: OpenTracker;
  first: string;
  betId?: string;
  setBetId: (betId?: string) => void;
}) {
  const bets = breakdownBets(data);
  const shown = betOf(bets, betId);
  const betsLine = betsListLine(data);
  const { rows, total } = participantWeekRows(data, shown?.id);
  const sequence = isSequence(data.config);
  const closeAs = data.participant.closeAs;
  const columns: TotalsColumn[] = sequence
    ? [
        { label: 'Sends', value: (r) => String(r.sends) },
        { label: 'Replies', value: (r) => String(r.replies) },
        { label: 'Convos booked', value: (r) => String(r.booked) },
        { label: 'Convos held', value: (r) => String(r.held) },
        { label: 'Closes', value: (r) => (closeAs ? String(r.closes) : '–') },
        { label: '$ booked', value: (r) => fmtMoney(r.bookedCents) },
        { label: '$ collected', value: (r) => fmtMoney(r.collectedCents) },
      ]
    : totalsColumns(data.config);
  return (
    <section class="flex flex-col gap-2">
      {betsLine && <p class="text-sm text-gray-700">{betsLine}</p>}
      {bets.length > 0 && <BetSwitch bets={bets} value={shown?.id} onChange={setBetId} />}
      <div class="overflow-x-auto border rounded-sm">
        <table class="w-full text-sm">
          <thead>
            <tr class="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wider">
              <Th>Week</Th>
              {columns.map((c) => (
                <Th key={c.label} class="text-right align-bottom">
                  {c.label}
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.weekStart}>
                <Td class="whitespace-nowrap">
                  <span class="block font-semibold text-gray-800">{weekLabel(row.weekNumber)}</span>
                  <span class="block text-xs text-gray-500">
                    {fmtWeekRange(row.weekStart)}
                    {row.isCurrent && ' · so far'}
                  </span>
                </Td>
                {columns.map((c) => (
                  <Td key={c.label} class="text-right">
                    {c.value(row.rollup)}
                  </Td>
                ))}
              </tr>
            ))}
            <tr class="font-semibold text-gray-800">
              <Td>Total</Td>
              {columns.map((c) => (
                <Td key={c.label} class="text-right">
                  {c.value(total)}
                </Td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      {sequence && <p class="text-sm text-gray-600">{closeChoiceNote(closeAs, first)}</p>}
    </section>
  );
}

function ScoreboardLine({ data }: { data: OpenTracker }) {
  const board = scoreboard(data.config, data.touches, data.participant);
  const betsLine = betsScoreboardLine(data);
  const target = board.kind === 'revenue' ? (data.participant.targetCents ?? 0) : 0;
  const percent =
    board.kind === 'revenue' && target > 0
      ? Math.min(100, Math.round((board.progressCents / target) * 100))
      : undefined;
  return (
    <section class="flex flex-col gap-2 border rounded-sm p-3" aria-label="Scoreboard">
      <strong class="text-gray-800">Scoreboard · {scoreboardLine(data)}</strong>
      {percent !== undefined && (
        <div
          class="h-2 rounded-full bg-gray-100 overflow-hidden"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          aria-label="Progress to target"
        >
          <div class="h-full bg-indigo-500" style={{ width: `${percent}%` }} />
        </div>
      )}
      {betsLine && <span class="text-sm text-gray-700 tabular-nums">{betsLine}</span>}
    </section>
  );
}

/**
 * Leads: the participant's destination, rate and target, then each of their
 * channels with its stages, so the coach reads the numbers below in their
 * own words.
 */
function LeadsFacts({ data, first }: { data: OpenTracker; first: string }) {
  const setUp = leadsSetUpLine(data.participant);
  return (
    <section class="flex flex-col gap-1 text-sm" aria-label="Set-up">
      <span class="text-gray-800">{setUp ?? `${first} hasn't set up the tracker yet.`}</span>
      {data.config.channels.length ? (
        <ul class="flex flex-col gap-0.5 text-gray-700">
          {data.config.channels.map((channel) => (
            <li key={channel.key}>
              <span class="font-semibold">{channel.label}</span>: {channelPath(channel)}
            </li>
          ))}
        </ul>
      ) : (
        <span class="text-gray-600">No channels mapped yet.</span>
      )}
    </section>
  );
}

function noop() {}

export function StudentTrackerTab({
  courseId,
  name,
  tracker,
  openPersonId,
  setOpenPersonId,
  retry,
}: {
  courseId: UUID;
  name: string;
  tracker?: CoachStudentTracker | 'failed';
  openPersonId?: string;
  setOpenPersonId(id?: string): void;
  retry(): void;
}) {
  const [betId, setBetId] = useState<string | undefined>();
  if (!tracker) {
    return <DefaultSpinner />;
  }
  if (tracker === 'failed') {
    return (
      <div class="mt-4 flex flex-col items-start gap-3" role="alert">
        <p class="text-sm text-gray-600">Couldn't load the tracker. Try again.</p>
        <BtnSecondary onClick={retry}>Retry</BtnSecondary>
      </div>
    );
  }
  if (tracker.ended) {
    return <TrackerEndedState lastDay={tracker.lastDay} today={tracker.today} name={name} />;
  }
  const first = firstNameOf(name);
  const open = openPersonId ? tracker.people.find((p) => p.id === openPersonId) : undefined;
  if (open) {
    const touches = tracker.touches.filter((t) => t.personId === open.id);
    const props = {
      data: tracker,
      courseId,
      person: open,
      touches,
      onResult: noop,
      onDeleted: noop,
      openLog: noop,
      close: noop,
      readOnly: true,
    };
    return (
      <div class="mt-4 flex flex-col gap-4">
        <Button
          class="self-start text-indigo-600 underline text-sm"
          onClick={() => setOpenPersonId(undefined)}
        >
          ← All people
        </Button>
        <h3 class="text-lg font-semibold text-gray-800">{personName(open)}</h3>
        <Facts {...props} />
        <Timeline {...props} />
      </div>
    );
  }
  const shownBet = betOf(breakdownBets(tracker), betId);
  const groups = coachPeopleGroups(shownBet ? betData(tracker, shownBet.id) : tracker);
  const leads = participantChannels(tracker.config);
  return (
    <div class="mt-4 flex flex-col gap-6">
      {leads && <LeadsFacts data={tracker} first={first} />}
      <WeeklyTable data={tracker} first={first} betId={shownBet?.id} setBetId={setBetId} />
      <ScoreboardLine data={tracker} />
      {leads && (
        <section class="flex flex-col gap-4" aria-label="Cost per lead">
          <TrendTable data={tracker} />
          <ByChannelTable data={tracker} />
        </section>
      )}
      <section class="flex flex-col gap-3" aria-label="People">
        {!tracker.people.length && (
          <p class="text-sm text-gray-600">
            No people yet. {first} hasn't added anyone to the tracker. Ask about it in a standup
            reply.
          </p>
        )}
        {groups.map((group) => (
          <section key={group.group} class="flex flex-col gap-2">
            <h3
              class={`text-sm font-semibold ${
                group.group === 'overdue' ? 'text-red-700' : 'text-gray-700'
              }`}
            >
              {group.title} ({group.people.length})
            </h3>
            <ul class="border rounded-sm bg-white">
              {group.people.map(({ person, next }) => (
                <PersonRowView
                  key={person.id}
                  data={tracker}
                  person={person}
                  next={next}
                  group={group.group}
                  openPerson={() => setOpenPersonId(person.id)}
                  betShown={!!shownBet}
                />
              ))}
            </ul>
          </section>
        ))}
      </section>
    </div>
  );
}
